import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ClipboardList, Calendar, Clock, Flame, Archive, Download, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { Profile } from '../../types';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import XLSX from 'xlsx-js-style';
import DateRangePicker from '../../src/components/DateRangePicker';
import { tipoSolicitacaoInfo } from './solicitacaoTipos';
import { contaNosCalculos } from '../../src/lib/prazoSolicitacaoVisual';

/* Mesma paleta de marca usada em TarefasKPIDashboard.tsx — mantém os dois
   dashboards de Tarefas visualmente consistentes. */
const BRAND = {
    olive: '#6b705c',
    olivePale: '#e8eae3',
    amber: '#d4a017',
    amberPale: '#fdf5e1',
    green: '#10b981',
    greenPale: '#d1fae5',
    red: '#dc2626',
    redPale: '#fee2e2',
    rose: '#e11d48', // mesmo tom de tarefas.prioridade === 'urgencia' (bg-rose-600) no resto do módulo
};

interface Props { isOpen: boolean; onClose: () => void; }

interface TarefaAudit {
    id: number; titulo: string; created_at: string; data_conclusao: string | null;
    prioridade: string; responsavel_id: string | null; solicitante_id: string | null; solicitacao_status: string | null;
    parent_id: number | null; tipo_solicitacao: string | null; bloqueada_por_pessoa_id?: string | null; bloqueada_por_texto?: string | null;
}
interface TimeEntryAudit { id: number; tarefa_id: number; user_id: string; started_at: string; ended_at: string | null; duration_seconds: number | null; }
interface StatusEventAudit { id: number; tarefa_id: number; user_id: string | null; from_status: string | null; to_status: string; created_at: string; }

const STAGE_ORDER = ['novo', 'em_analise', 'em_desenvolvimento', 'em_teste'];
const STATUS_LABELS: Record<string, string> = { novo: 'Novo', em_analise: 'Em Análise', em_desenvolvimento: 'Em Desenvolvimento', em_teste: 'Em Teste', concluido: 'Concluído', rejeitado: 'Rejeitado' };
const PRIORITY_ORDER = ['baixa', 'media', 'alta', 'urgencia'];
const PRIORITY_LABELS: Record<string, string> = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgencia: 'Urgência' };

const fmtHours = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.round((totalSeconds % 3600) / 60);
    if (h === 0 && m === 0) return '0min';
    if (h === 0) return `${m}min`;
    return `${h}h ${m}min`;
};
const fmtDias = (dias: number) => dias.toFixed(1).replace('.0', '') + (dias < 2 ? ' dia' : ' dias');
const fmtP = (v: number) => v.toFixed(1) + '%';

const StatTile: React.FC<{ label: string; value: string; icon: React.ElementType; color: string; bg: string }> = ({ label, value, icon: Icon, color, bg }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-center gap-3">
        <div className="p-2.5 rounded-xl shrink-0" style={{ background: bg, color }}><Icon size={18} /></div>
        <div className="min-w-0">
            <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
        </div>
    </div>
);

const ChartCard: React.FC<{ title: string; empty?: boolean; children: React.ReactNode }> = ({ title, empty, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
        <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-3">{title}</h4>
        {empty ? (
            <div className="flex-1 min-h-[180px] flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 italic">
                Ainda sem dados suficientes no período.
            </div>
        ) : children}
    </div>
);

const SolicitacoesAuditDashboard: React.FC<Props> = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [tarefas, setTarefas] = useState<TarefaAudit[]>([]);
    const [timeEntries, setTimeEntries] = useState<TimeEntryAudit[]>([]);
    const [statusEvents, setStatusEvents] = useState<StatusEventAudit[]>([]);
    const [profiles, setProfiles] = useState<Profile[]>([]);

    const n = new Date();
    const [dateStart, setDateStart] = useState(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().split('T')[0]);
    const [dateEnd, setDateEnd] = useState(new Date(n.getFullYear(), n.getMonth() + 1, 0).toISOString().split('T')[0]);

    useEffect(() => { if (isOpen) fetchData(); }, [isOpen]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tarefasRes, timeRes, eventsRes, profilesRes] = await Promise.all([
                supabase.from('tarefas').select('id,titulo,created_at,data_conclusao,prioridade,responsavel_id,solicitante_id,solicitacao_status,parent_id,tipo_solicitacao,bloqueada_por_pessoa_id,bloqueada_por_texto').eq('categoria', 'solicitacao')
                    // Painel das reuniões de Tecnologia e Marketing: as equipes novas da Central (#6518) ficam de fora.
                    // Sem departamento = solicitação antiga, que era sempre dessa fila.
                    .or('departamento_solicitacao.is.null,departamento_solicitacao.in.(Tecnologia,Marketing)'),
                supabase.from('tarefa_time_entries').select('id,tarefa_id,user_id,started_at,ended_at,duration_seconds'),
                supabase.from('tarefa_status_events').select('id,tarefa_id,user_id,from_status,to_status,created_at'),
                supabase.from('profiles').select('*').eq('status', 'active').neq('role', 'consumidor'),
            ]);
            const ids = new Set((tarefasRes.data || []).map((t: any) => t.id));
            setTarefas((tarefasRes.data || []) as TarefaAudit[]);
            setTimeEntries(((timeRes.data || []) as TimeEntryAudit[]).filter(e => ids.has(e.tarefa_id)));
            setStatusEvents(((eventsRes.data || []) as StatusEventAudit[]).filter(e => ids.has(e.tarefa_id)));
            setProfiles((profilesRes.data || []) as Profile[]);
        } catch (err) {
            console.error('Audit dashboard fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const inRange = (dateStr: string) => {
        const d = new Date(dateStr);
        return d >= new Date(dateStart + 'T00:00:00') && d <= new Date(dateEnd + 'T23:59:59');
    };

    const computed = useMemo(() => {
        // #6353: rejeitada e bloqueada no momento ("depende de") não entram nos números.
        // A taxa de rejeição é a exceção: sem as rejeitadas ela não existe.
        const validas = tarefas.filter(contaNosCalculos);
        const idsValidos = new Set(validas.map(t => t.id));
        const criadasNoPeriodo = validas.filter(t => inRange(t.created_at));
        const finalizadasNoPeriodo = tarefas.filter(t => t.data_conclusao && inRange(t.data_conclusao) && (t.solicitacao_status === 'concluido' || t.solicitacao_status === 'rejeitado'));
        const concluidasNoPeriodo = finalizadasNoPeriodo.filter(t => t.solicitacao_status === 'concluido');
        const rejeitadasNoPeriodo = finalizadasNoPeriodo.filter(t => t.solicitacao_status === 'rejeitado');
        const taxaRejeicao = finalizadasNoPeriodo.length > 0 ? (rejeitadasNoPeriodo.length / finalizadasNoPeriodo.length) * 100 : 0;

        const backlogAtual = validas.filter(t => !t.responsavel_id && t.solicitacao_status !== 'concluido' && t.solicitacao_status !== 'rejeitado').length;

        const leadTimes = validas
            .filter(t => t.data_conclusao && t.solicitacao_status === 'concluido' && inRange(t.data_conclusao))
            .map(t => ({ prioridade: t.prioridade, dias: Math.max(0, (new Date(t.data_conclusao!).getTime() - new Date(t.created_at).getTime()) / (1000 * 60 * 60 * 24)) }));
        const avgLeadTime = leadTimes.length > 0 ? leadTimes.reduce((a, b) => a + b.dias, 0) / leadTimes.length : 0;
        const leadTimeByPriority = PRIORITY_ORDER.map(p => {
            const items = leadTimes.filter(l => l.prioridade === p);
            return { name: PRIORITY_LABELS[p], dias: items.length > 0 ? Math.round((items.reduce((a, b) => a + b.dias, 0) / items.length) * 10) / 10 : 0, temDados: items.length > 0 };
        });

        // Tempo médio em cada etapa: diferença entre eventos consecutivos de tarefa_status_events
        const eventsByTarefa: Record<number, StatusEventAudit[]> = {};
        statusEvents.filter(e => idsValidos.has(e.tarefa_id)).forEach(e => { (eventsByTarefa[e.tarefa_id] ||= []).push(e); });
        const stageDurationsHoras: Record<string, number[]> = {};
        Object.values(eventsByTarefa).forEach(evs => {
            const sorted = [...evs].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
            for (let i = 0; i < sorted.length - 1; i++) {
                const stage = sorted[i].to_status;
                if (!STAGE_ORDER.includes(stage)) continue;
                const horas = (new Date(sorted[i + 1].created_at).getTime() - new Date(sorted[i].created_at).getTime()) / (1000 * 60 * 60);
                (stageDurationsHoras[stage] ||= []).push(horas);
            }
        });
        const stageDurationChart = STAGE_ORDER.map(s => {
            const arr = stageDurationsHoras[s] || [];
            const avg = arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
            return { name: STATUS_LABELS[s], horas: Math.round(avg * 10) / 10 };
        });
        const hasStageData = Object.values(stageDurationsHoras).some(arr => arr.length > 0);

        // Tendência semanal — criadas/concluídas (throughput) e urgências criadas
        const start = new Date(dateStart + 'T00:00:00');
        const end = new Date(dateEnd + 'T23:59:59');
        const weeks: { week: string; criadas: number; concluidas: number; urgencias: number }[] = [];
        let wn = 1; let ws = new Date(start);
        while (ws <= end) {
            const we = new Date(ws); we.setDate(we.getDate() + 6); if (we > end) we.setTime(end.getTime());
            const wsS = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate(), 0, 0, 0);
            const wsE = new Date(we.getFullYear(), we.getMonth(), we.getDate(), 23, 59, 59);
            weeks.push({
                week: `Sem ${wn}`,
                criadas: validas.filter(t => { const d = new Date(t.created_at); return d >= wsS && d <= wsE; }).length,
                concluidas: validas.filter(t => { if (!t.data_conclusao || t.solicitacao_status !== 'concluido') return false; const d = new Date(t.data_conclusao); return d >= wsS && d <= wsE; }).length,
                urgencias: validas.filter(t => { const d = new Date(t.created_at); return d >= wsS && d <= wsE && t.prioridade === 'urgencia'; }).length,
            });
            wn++; ws = new Date(we); ws.setDate(ws.getDate() + 1);
        }

        // Ranking por colaborador
        const colaboradorStats = profiles.map(p => {
            const meusEntries = timeEntries.filter(e => e.user_id === p.id && e.duration_seconds != null && inRange(e.started_at));
            const totalSeconds = meusEntries.reduce((a, e) => a + (e.duration_seconds || 0), 0);
            const minhasConcluidas = validas.filter(t => t.responsavel_id === p.id && t.solicitacao_status === 'concluido' && t.data_conclusao && inRange(t.data_conclusao));
            const minhasUrgencias = validas.filter(t => t.responsavel_id === p.id && t.prioridade === 'urgencia' && inRange(t.created_at));
            return { id: p.id, nome: p.full_name, totalSeconds, concluidas: minhasConcluidas.length, urgencias: minhasUrgencias.length };
        }).filter(s => s.totalSeconds > 0 || s.concluidas > 0 || s.urgencias > 0)
            .sort((a, b) => b.totalSeconds - a.totalSeconds);

        // Ranking por solicitante — quem mais abre chamados no período e por qual motivo (tipo_solicitacao)
        const solicitanteMap: Record<string, { id: string; nome: string; total: number; porTipo: Record<string, number> }> = {};
        criadasNoPeriodo.forEach(t => {
            if (!t.solicitante_id) return;
            if (!solicitanteMap[t.solicitante_id]) {
                const p = profiles.find(pp => pp.id === t.solicitante_id);
                solicitanteMap[t.solicitante_id] = { id: t.solicitante_id, nome: p?.full_name || 'Usuário removido', total: 0, porTipo: {} };
            }
            const entry = solicitanteMap[t.solicitante_id];
            entry.total++;
            const tipoKey = t.tipo_solicitacao || 'sem_tipo';
            entry.porTipo[tipoKey] = (entry.porTipo[tipoKey] || 0) + 1;
        });
        const solicitanteStats = Object.values(solicitanteMap)
            .map(s => {
                const motivos = Object.entries(s.porTipo).sort((a, b) => b[1] - a[1]);
                const [topTipo, topCount] = motivos[0] || [null, 0];
                const motivoPrincipal = topTipo && topTipo !== 'sem_tipo'
                    ? (tipoSolicitacaoInfo(topTipo as any)?.label || topTipo)
                    : (topTipo === 'sem_tipo' ? 'Sem tipo definido' : '—');
                return { ...s, motivoPrincipal, motivoPrincipalCount: topCount };
            })
            .sort((a, b) => b.total - a.total);

        return {
            criadasNoPeriodo: criadasNoPeriodo.length, concluidasNoPeriodo: concluidasNoPeriodo.length,
            taxaRejeicao, backlogAtual, avgLeadTime, leadTimeByPriority,
            stageDurationChart, hasStageData, weeks, colaboradorStats, solicitanteStats,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tarefas, timeEntries, statusEvents, profiles, dateStart, dateEnd]);

    const handleExport = () => {
        const wb = XLSX.utils.book_new();

        const resumo = [
            { Métrica: 'Criadas no período', Valor: computed.criadasNoPeriodo },
            { Métrica: 'Concluídas no período', Valor: computed.concluidasNoPeriodo },
            { Métrica: 'Taxa de rejeição', Valor: fmtP(computed.taxaRejeicao) },
            { Métrica: 'Lead time médio', Valor: fmtDias(computed.avgLeadTime) },
            { Métrica: 'Backlog atual (sem responsável)', Valor: computed.backlogAtual },
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumo), 'Resumo');

        const porColaborador = computed.colaboradorStats.map(s => ({
            Colaborador: s.nome, 'Tempo total': fmtHours(s.totalSeconds),
            'Solicitações concluídas': s.concluidas, 'Urgências tratadas': s.urgencias,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porColaborador), 'Por Colaborador');

        const porSolicitante = computed.solicitanteStats.map(s => ({
            Solicitante: s.nome, 'Total de chamados': s.total,
            'Motivo mais comum': s.motivoPrincipal, 'Qtd. desse motivo': s.motivoPrincipalCount,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porSolicitante), 'Por Solicitante');

        const semanal = computed.weeks.map(w => ({ Semana: w.week, Criadas: w.criadas, Concluídas: w.concluidas, Urgências: w.urgencias }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(semanal), 'Semanal');

        XLSX.writeFile(wb, `auditoria-solicitacoes_${dateStart}_a_${dateEnd}.xlsx`);
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-2 sm:p-4 md:p-6">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} style={{ animation: 'fadeIn .2s ease' }} />
            <div className="relative w-full max-w-6xl my-auto" style={{ animation: 'slideUp .3s ease' }}>
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden border border-gray-100 dark:border-gray-800">

                    <div className="relative z-10 px-5 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0" style={{ background: `linear-gradient(135deg, ${BRAND.olivePale}80, ${BRAND.amberPale}60)` }}>
                        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-white/60 rounded-lg transition-colors z-10"><X size={20} /></button>
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 pr-10">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl flex-shrink-0" style={{ background: BRAND.olive, color: '#fff' }}><ClipboardList size={20} /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800 dark:text-white tracking-tight">Auditoria de Solicitações</h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Tempo gasto, urgências e ritmo da fila — base para reuniões de Tecnologia e Marketing</p>
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                                <div className="bg-white/80 dark:bg-gray-800 p-2 rounded-xl border border-gray-200/60 dark:border-gray-700 shadow-sm backdrop-blur-sm">
                                    <DateRangePicker
                                        startDate={dateStart}
                                        endDate={dateEnd}
                                        onChange={(s, e) => { if (s && e) { setDateStart(s); setDateEnd(e); } }}
                                        align="right"
                                    />
                                </div>
                                <button onClick={handleExport} disabled={loading} className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg transition-colors disabled:opacity-50" style={{ background: BRAND.olivePale, color: BRAND.olive }}>
                                    <Download size={13} /> Exportar
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 md:p-5 bg-gray-50 dark:bg-gray-950/40">
                        {loading ? (
                            <div className="flex items-center justify-center py-24">
                                <Loader2 size={28} className="animate-spin text-gray-300" />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                                    <StatTile label="Criadas no período" value={String(computed.criadasNoPeriodo)} icon={Calendar} color={BRAND.olive} bg={BRAND.olivePale} />
                                    <StatTile label="Concluídas no período" value={String(computed.concluidasNoPeriodo)} icon={ClipboardList} color={BRAND.green} bg={BRAND.greenPale} />
                                    <StatTile label="Taxa de rejeição" value={fmtP(computed.taxaRejeicao)} icon={X} color={BRAND.red} bg={BRAND.redPale} />
                                    <StatTile label="Lead time médio" value={computed.avgLeadTime > 0 ? fmtDias(computed.avgLeadTime) : '—'} icon={Clock} color={BRAND.amber} bg={BRAND.amberPale} />
                                    <StatTile label="Backlog atual" value={String(computed.backlogAtual)} icon={Archive} color={BRAND.rose} bg={BRAND.redPale} />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <ChartCard title="Criadas vs. Concluídas por semana" empty={computed.weeks.every(w => w.criadas === 0 && w.concluidas === 0)}>
                                        <ResponsiveContainer width="100%" height={220}>
                                            <BarChart data={computed.weeks} barGap={2}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                                <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#9ca3af" width={28} />
                                                <Tooltip />
                                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                                <Bar dataKey="criadas" name="Criadas" fill={BRAND.amber} radius={[4, 4, 0, 0]} />
                                                <Bar dataKey="concluidas" name="Concluídas" fill={BRAND.olive} radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>

                                    <ChartCard title="Urgências criadas por semana" empty={computed.weeks.every(w => w.urgencias === 0)}>
                                        <ResponsiveContainer width="100%" height={220}>
                                            <BarChart data={computed.weeks} barGap={2}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                                <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#9ca3af" width={28} />
                                                <Tooltip />
                                                <Bar dataKey="urgencias" name="Urgências" fill={BRAND.rose} radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <ChartCard title="Tempo médio em cada etapa" empty={!computed.hasStageData}>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <BarChart data={computed.stageDurationChart} layout="vertical" margin={{ left: 8 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                                                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" unit="h" />
                                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" width={110} />
                                                <Tooltip formatter={(v: number) => [`${v}h`, 'Tempo médio']} />
                                                <Bar dataKey="horas" name="Horas" fill={BRAND.olive} radius={[0, 4, 4, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>

                                    <ChartCard title="Lead time médio por prioridade" empty={computed.leadTimeByPriority.every(p => !p.temDados)}>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <BarChart data={computed.leadTimeByPriority}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                                                <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" width={28} unit="d" />
                                                <Tooltip formatter={(v: number) => [`${v} dias`, 'Lead time']} />
                                                <Bar dataKey="dias" name="Dias" fill={BRAND.amber} radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                                    <h4 className="text-sm font-bold text-gray-800 dark:text-white px-4 pt-4 pb-2">Ranking por colaborador</h4>
                                    {computed.colaboradorStats.length === 0 ? (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 italic px-4 pb-4">Ainda sem cronômetros ou conclusões registradas no período.</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs font-medium border-y border-gray-100 dark:border-gray-700">
                                                    <tr>
                                                        <th className="px-4 py-2">Colaborador</th>
                                                        <th className="px-4 py-2">Tempo total</th>
                                                        <th className="px-4 py-2">Concluídas</th>
                                                        <th className="px-4 py-2">Urgências tratadas</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                                    {computed.colaboradorStats.map(s => (
                                                        <tr key={s.id}>
                                                            <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{s.nome}</td>
                                                            <td className="px-4 py-2.5 font-mono text-gray-600 dark:text-gray-300">{fmtHours(s.totalSeconds)}</td>
                                                            <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{s.concluidas}</td>
                                                            <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                                                                {s.urgencias > 0 && <Flame size={12} className="inline mr-1 text-rose-500" />}
                                                                {s.urgencias}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                                    <h4 className="text-sm font-bold text-gray-800 dark:text-white px-4 pt-4 pb-2">Quem mais abre chamados</h4>
                                    {computed.solicitanteStats.length === 0 ? (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 italic px-4 pb-4">Nenhum chamado criado no período.</p>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs font-medium border-y border-gray-100 dark:border-gray-700">
                                                    <tr>
                                                        <th className="px-4 py-2">Solicitante</th>
                                                        <th className="px-4 py-2">Total de chamados</th>
                                                        <th className="px-4 py-2">Motivo mais comum</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                                    {computed.solicitanteStats.map(s => (
                                                        <tr key={s.id}>
                                                            <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{s.nome}</td>
                                                            <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{s.total}</td>
                                                            <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                                                                {s.motivoPrincipal}
                                                                {s.motivoPrincipalCount > 0 && <span className="text-gray-400 dark:text-gray-500"> ({s.motivoPrincipalCount})</span>}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SolicitacoesAuditDashboard;
